import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Facebook, Instagram, Music2 } from "lucide-react";
import { LOGO } from "@/lib/api";

const CONTACT = {
  branches: [
    { name: "Pretoria North", lines: ["Shop 16 North Park Mall", "256 Burger Street", "Pretoria North"], contact: null, phone: "061 081 4598" },
    { name: "Bloemfontein", lines: ["Uni Park Building", "Nobel Street, Brandwag", "Bloemfontein, 9301"], contact: "Tumi", phone: "073 432 6408" },
  ],
  phone: "061 081 4598",
  whatsapp: "https://wa.me/27610814598",
  email: "orders@btmedu.co.za",
  socials: [
    { name: "Facebook", handle: "btmedu stationery shop", href: "https://www.facebook.com/btmedustationeryshop", Icon: Facebook },
    { name: "Instagram", handle: "BTMEDU Stationery Shop", href: "https://www.instagram.com/btmedustationeryshop/", Icon: Instagram },
    { name: "TikTok", handle: "@btmedu_stationery", href: "https://www.tiktok.com/@btmedu_stationery", Icon: Music2 },
  ],
};

export function Footer() {
  return (
    <footer className="bg-navy-dark text-white mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-4">
        <div>
          <span className="bg-white rounded-lg px-2.5 py-1.5 inline-flex mb-4">
            <img src={LOGO} alt="BTMEDU" className="h-9 w-auto" />
          </span>
          <p className="text-sm text-white/60 leading-relaxed">
            Your school and stationery shopping destination. Supporting learners, parents, teachers and
            schools with everyday essentials.
          </p>
        </div>
        <div>
          <h4 className="font-display font-bold text-sm uppercase tracking-wider text-gold mb-4">Shop</h4>
          <ul className="space-y-2.5 text-sm text-white/70">
            <li><Link to="/shop/stationery" className="hover:text-white transition-colors" data-testid="footer-stationery">Stationery</Link></li>
            <li><Link to="/shop/textbooks" className="hover:text-white transition-colors" data-testid="footer-textbooks">Textbooks</Link></li>
            <li><Link to="/shop/religious-books" className="hover:text-white transition-colors" data-testid="footer-religious">Religious Books</Link></li>
            <li><Link to="/specials" className="hover:text-white transition-colors" data-testid="footer-specials">Specials</Link></li>
            <li><Link to="/schools" className="hover:text-white transition-colors" data-testid="footer-schools">School & bulk orders</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-display font-bold text-sm uppercase tracking-wider text-gold mb-4">Customer care</h4>
          <ul className="space-y-2.5 text-sm text-white/70">
            <li><Link to="/quote" className="hover:text-white transition-colors" data-testid="footer-quote">Request a quotation</Link></li>
            <li><Link to="/account" className="hover:text-white transition-colors" data-testid="footer-account">My orders</Link></li>
            <li><Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-delivery">Delivery & collection</Link></li>
            <li><Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-returns">Returns policy</Link></li>
            <li><Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy (POPIA)</Link></li>
            <li><Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms & conditions</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-display font-bold text-sm uppercase tracking-wider text-gold mb-4">Contact BTMEDU</h4>
          <ul className="space-y-3 text-sm text-white/70">
            {CONTACT.branches.map((b) => (
              <li key={b.name} className="flex gap-2.5">
                <MapPin size={15} className="text-gold shrink-0 mt-0.5" />
                <span data-testid={`footer-branch-${b.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="block font-bold text-white/90">{b.name}</span>
                  {b.lines.map((l) => <span key={l} className="block">{l}</span>)}
                  <span className="block text-white/50 text-xs mt-0.5">{b.contact ? `${b.contact} · ` : ""}{b.phone}</span>
                </span>
              </li>
            ))}
            <li>
              <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="flex gap-2.5 hover:text-white transition-colors" data-testid="footer-whatsapp">
                <Phone size={15} className="text-gold shrink-0 mt-0.5" />
                <span>Call / WhatsApp<br />{CONTACT.phone}</span>
              </a>
            </li>
            <li>
              <a href={`mailto:${CONTACT.email}`} className="flex gap-2.5 hover:text-white transition-colors" data-testid="footer-email">
                <Mail size={15} className="text-gold shrink-0 mt-0.5" />
                <span>{CONTACT.email}</span>
              </a>
            </li>
          </ul>
          <div className="flex gap-2 mt-4">
            {CONTACT.socials.map(({ name, handle, href, Icon }) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={`${name}: ${handle}`}
                aria-label={`${name} — ${handle}`}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-gold hover:text-white flex items-center justify-center text-white/80 transition-colors"
                data-testid={`footer-social-${name.toLowerCase()}`}
              >
                <Icon size={15} />
              </a>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-white/40 leading-relaxed" data-testid="footer-social-handles">
            {CONTACT.socials.map((s) => `${s.name}: ${s.handle}`).join(" · ")}
          </p>
          <Link
            to="/quote"
            className="inline-flex mt-4 bg-gold hover:bg-gold-hover text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors"
            data-testid="footer-cta-quote"
          >
            Bulk quotation
          </Link>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between text-xs text-white/40">
          <span>© BTMEDU. All rights reserved.</span>
          <span className="font-display font-extrabold tracking-tight text-white/25 text-sm">
            BUILT FOR LEARNERS · DESIGNED FOR EASY SHOPPING
          </span>
        </div>
      </div>
    </footer>
  );
}
